import { useState, useEffect } from "react";
import { CSS } from "../App";
import { AgreementText } from "./AgreementText";
import { PdfPages } from "./PdfPages";
import { SignHereOverlay } from "./FieldBoxes";
import { DrawPad } from "./DrawPad";

// ─── Public signing page: /sign/<token> ───────────────────────────────────────
// What a customer sees from the link in their email. No CamStorage login needed.

export function SignPage({ token }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [typed, setTyped] = useState("");
  const [drawn, setDrawn] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`/api/esign?action=doc&token=${encodeURIComponent(token)}`)
      .then(async r => { const d = await r.json(); if (d.error && !d.title) setError(d.error); else setDoc(d); if (d.error) setError(d.error); })
      .catch(() => setError("We couldn't load this document. Please check your connection and try again."));
  }, [token]);

  async function sign(e) {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await fetch(`/api/esign?action=sign&token=${encodeURIComponent(token)}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ typedName: typed, consent, signatureImage: drawn }),
    });
    const d = await r.json().catch(() => ({ error: "Something went wrong" }));
    setBusy(false);
    if (!r.ok || d.error) { setError(d.error || "Signing failed"); return; }
    setDone(d);
  }

  async function download() {
    const d = await (await fetch(`/api/esign?action=signed&token=${encodeURIComponent(token)}`)).json();
    if (d.url) window.location.href = d.url;
  }

  const signed = done || doc?.status === "signed" || doc?.status === "customer_signed";
  const awaitingBusiness = done ? done.awaitingBusiness : doc?.status === "customer_signed";   // the business signs next
  return (
    <>
      <style>{CSS}</style>
      <div style={{ minHeight: "100vh", background: "var(--mist)", padding: "24px 16px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ fontSize: 13, color: "var(--sub)", marginBottom: 4 }}>{doc?.businessName || "CamStorage"}</div>
          <h1 style={{ fontFamily: "var(--fh)", color: "var(--navy)", fontSize: 24, marginBottom: 16 }}>{doc?.title || "Document to sign"}</h1>

          {!doc && !error && <div className="card"><div className="cb">Loading…</div></div>}
          {error && !signed && <div className="login-err" style={{ fontSize: 14 }}>{error}</div>}

          {signed && (
            <div className="card"><div className="cb" style={{ textAlign: "center", padding: 30 }}>
              <div style={{ fontSize: 40 }}>✅</div>
              <h2 style={{ color: "var(--navy)", margin: "8px 0" }}>Signed — thank you{doc?.signerName ? `, ${doc.signerName.split(" ")[0]}` : ""}</h2>
              {awaitingBusiness
                ? <p className="tsub">{doc?.businessName || "The sender"} will now sign it too. As soon as they have, we'll email you the final signed copy.</p>
                : <>
                    <p className="tsub">A copy has been emailed to you and to {doc?.businessName || "the sender"}.</p>
                    <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={download}>Download your signed copy</button>
                  </>}
            </div></div>
          )}

          {doc && !signed && !error && (
            <>
              <div className="card"><div className="cb">
                {doc.kind === "text"
                  ? <AgreementText text={doc.body} style={{ maxHeight: "65vh", overflow: "auto" }} />
                  : <>
                      <PdfPages source={doc.pdfUrl} maxHeight="70vh" overlay={page => <SignHereOverlay page={page} boxes={doc.signatureBoxes || []} />} />
                      <a href={doc.pdfUrl} target="_blank" rel="noopener noreferrer" className="tsm">Open the agreement in a new tab</a>
                    </>}
                {(doc.kind === "text" ? /\{\{\s*signature\s*\}\}/.test(doc.body || "") : doc.signatureBoxes?.length > 0) &&
                  <p className="tsm" style={{ marginTop: 8, color: "#7A5600" }}>Your signature, name and today's date will be added where it says “Sign here”.</p>}
              </div></div>

              <form className="card" onSubmit={sign}><div className="cb">
                <h3 style={{ color: "var(--navy)", marginBottom: 6 }}>Sign</h3>
                <p className="tsub tsm" style={{ marginBottom: 12 }}>Signing as <strong>{doc.signerName}</strong>.</p>
                <div className="login-field"><label>Type your full name</label>
                  <input value={typed} onChange={e => setTyped(e.target.value)} placeholder={doc.signerName} autoComplete="name" /></div>
                {typed.trim().length > 1 && <div style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 28, color: "var(--navy)", margin: "4px 0 12px" }}>{typed}</div>}
                <div className="login-field"><label>Draw your signature</label><DrawPad onChange={setDrawn} /></div>
                <label style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 14, margin: "14px 0", padding: "14px 16px", cursor: "pointer", borderRadius: 10,
                  background: consent ? "#EBF5F0" : "#FFF8E6", border: `2px solid ${consent ? "var(--success)" : "#E8B931"}` }}>
                  <input type="checkbox" style={{ width: 26, height: 26, minWidth: 26, margin: 0, accentColor: "var(--navy)", cursor: "pointer" }} checked={consent} onChange={e => setConsent(e.target.checked)} />
                  <span>I have read this agreement and I agree to sign it electronically. I understand my electronic signature has the same effect as signing by hand.</span>
                </label>
                <button className="login-btn" disabled={busy || !consent || typed.trim().length < 2 || !drawn}>{busy ? "Signing…" : "Sign agreement"}</button>
                {!busy && (typed.trim().length < 2 || !drawn || !consent) && <p className="tsm" style={{ marginTop: 8, color: "#8A6100" }}>{typed.trim().length < 2 ? "Type your full name above" : !drawn ? "Draw your signature in the box above" : "Tick the box above"} to sign.</p>}
                <p className="tsub tsm" style={{ marginTop: 10 }}>We record the time, your IP address and browser with your signature, and a fingerprint of the document, in a certificate attached to the signed copy.</p>
              </div></form>
            </>
          )}
        </div>
      </div>
    </>
  );
}
